import { erc20Abi, getAddress, zeroAddress } from 'viem';

import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import type { BalanceLightResponse, BalanceResponse } from '@/lib/api/speed';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';
import { hlInfo } from '@/lib/hyperliquidInfo';

/**
 * EOA-direct HyperEVM withdrawal — the user's own wallet signs every transaction, on chain 999,
 * with no service wallet, no backend job and no bridge involved:
 *
 *   1. `CustodialWallet.withdrawErc20(usdc, eoa, amount)` — owner-only, already live in every
 *      deployed wallet. Custodial USDC goes straight to the EOA.
 *   2. `HyperliquidManager.selfCorePerpToSpot(notional)` — CoreWriter usdClassTransfer moving the
 *      adapter's free perp collateral to its Core spot account (skipped for unified accounts,
 *      whose balance already sits in spot).
 *   3. `HyperliquidManager.selfCoreSpotSweep(coreWei)` — CoreWriter spotSend to the USDC system
 *      address; HyperCore credits the linked ERC20 to the ADAPTER's EVM address.
 *   4. `HyperliquidManager.selfFlush()` — the adapter's whole EVM USDC balance to the EOA.
 *
 * Every Core action is asynchronous (the credit lands in a later Core block), so the executor
 * polls between steps. Dust-proven live 2026-07-29 with SmartContracts/CoreEvmProbe.
 *
 * This route sweeps EVERYTHING — it exists to empty the trading stack back into the user's own
 * wallet. Partial amounts are not offered: the adapter can only flush its full balance (deployed
 * per-user adapters are not upgradeable), so a partial direct withdrawal would silently
 * over-deliver.
 */

export const HYPER_EVM_MANAGER_ABI = [
    {
        type: 'function',
        name: 'selfCorePerpToSpot',
        stateMutability: 'nonpayable',
        inputs: [{ name: 'notional', type: 'uint64' }],
        outputs: [],
    },
    {
        type: 'function',
        name: 'selfCoreSpotSweep',
        stateMutability: 'nonpayable',
        inputs: [{ name: 'coreWei', type: 'uint64' }],
        outputs: [],
    },
    {
        type: 'function',
        name: 'selfFlush',
        stateMutability: 'nonpayable',
        inputs: [],
        outputs: [{ name: 'amount', type: 'uint256' }],
    },
] as const;

export const CUSTODIAL_WALLET_WITHDRAW_ABI = [
    {
        type: 'function',
        name: 'withdrawErc20',
        stateMutability: 'nonpayable',
        inputs: [
            { name: '_token', type: 'address' },
            { name: '_to', type: 'address' },
            { name: '_amount', type: 'uint256' },
        ],
        outputs: [],
    },
] as const;

/**
 * Minimum HYPE the EOA must hold to sign the route. A step costs ~150k gas and HyperEVM small
 * blocks price around 0.1 gwei (~0.000015 HYPE per tx); 0.001 covers the whole route with two
 * orders of magnitude of headroom for gas-price spikes, while still being fractions of a cent.
 */
export const MIN_HYPE_FOR_GAS_WEI = 1_000_000_000_000_000n; // 0.001 HYPE

/**
 * Core spot kept behind on a sweep, in core 8dp. HyperCore charges the SENDER a small transfer
 * gas (~$0.001 measured); a send of the full balance is dropped for insufficient gas, so a cent
 * stays behind in the user's own adapter account.
 */
export const CORE_SWEEP_GAS_BUFFER_8 = 1_000_000n; // $0.01

/** Below this there is nothing worth moving: the core buffer would eat the whole sweep. */
export const MIN_CORE_SWEEP_8 = 2_000_000n; // $0.02

export type DirectWithdrawStep =
    | { kind: 'custodialWithdraw'; amount6: bigint }
    | { kind: 'corePerpToSpot'; notional6: bigint }
    | { kind: 'coreSpotSweep' }
    | { kind: 'flush' };

export interface DirectWithdrawPlan {
    steps: DirectWithdrawStep[];
    /** Total the plan sets out to deliver (6dp; the core part is an estimate before fees). */
    total6: bigint;
}

/** Human labels for the step chip; index/total come from the plan itself. */
export const DIRECT_STEP_LABELS: Record<DirectWithdrawStep['kind'], string> = {
    custodialWithdraw: 'Withdrawing from your wallet',
    corePerpToSpot: 'Freeing your trading balance',
    coreSpotSweep: 'Moving funds out of HyperCore',
    flush: 'Collecting funds to your wallet',
};

/**
 * Builds the transaction sequence from the same on-chain balance snapshot the panel displays.
 * Balances are re-read where it matters at execution time (the sweep amount depends on what the
 * class transfer actually credited); this decides only WHICH steps exist.
 */
export function planDirectWithdraw(input: {
    balance: Pick<
        BalanceResponse,
        'custodialUsdc' | 'adapterUsdc' | 'hyperCoreWithdrawable'
    >;
    unified: boolean;
}): DirectWithdrawPlan {
    const custodial6 = BigInt(input.balance.custodialUsdc || '0');
    const adapter6 = BigInt(input.balance.adapterUsdc || '0');
    const core6 = BigInt(input.balance.hyperCoreWithdrawable || '0');

    const steps: DirectWithdrawStep[] = [];
    if (custodial6 > 0n) {
        steps.push({ kind: 'custodialWithdraw', amount6: custodial6 });
    }
    // the core balance only matters when it clears the buffer floor — below that the transfer
    // gas buffer would eat the sweep and the extra signatures would move nothing.
    const core8 = core6 * 100n;
    const coreWorthMoving = core8 >= MIN_CORE_SWEEP_8;
    if (coreWorthMoving) {
        if (!input.unified) {
            // non-unified adapters hold the balance as free PERP collateral; unified accounts
            // already sit in spot, so the class transfer would be a no-op HyperCore drops.
            steps.push({ kind: 'corePerpToSpot', notional6: core6 });
        }
        steps.push({ kind: 'coreSpotSweep' });
    }
    // the sweep credits the ADAPTER's evm balance, so any core movement needs the flush too.
    if (adapter6 > 0n || coreWorthMoving) {
        steps.push({ kind: 'flush' });
    }

    const coreDelivered6 = coreWorthMoving
        ? (core8 - CORE_SWEEP_GAS_BUFFER_8) / 100n
        : 0n;
    return { steps, total6: custodial6 + adapter6 + coreDelivered6 };
}

export type DirectWithdrawBlocker = 'noFunds' | 'insufficientGas';

/** Why the direct route cannot run right now, or null when it can. */
export function directWithdrawBlocker(input: {
    plan: DirectWithdrawPlan;
    hypeBalanceWei: bigint;
}): DirectWithdrawBlocker | null {
    if (input.plan.steps.length === 0) {
        return 'noFunds';
    }
    if (input.hypeBalanceWei < MIN_HYPE_FOR_GAS_WEI) {
        return 'insufficientGas';
    }
    return null;
}

export type WithdrawDestination = 'hyperEvm' | AnchorChainId;

/**
 * What the withdraw form preselects while the user has not touched the picker.
 *
 * HyperEVM is the better route (user-signed, no bridge, no minimum) but the user pays its gas
 * in HYPE, so preselecting it for a wallet with no HYPE lands them on a form whose only button
 * is disabled. Those users start on the bridged route to the preferred anchor (the chain their
 * wallet sits on) instead; the gas read is asynchronous, so `null` (still loading) also starts
 * bridged and flips once it comes back positive.
 */
export function defaultWithdrawDestination(input: {
    /** The direct route is wired at all (controls present). */
    directAvailable: boolean;
    hypeGasOk: boolean | null;
    /** Bridged fallback chain; absent means Cronos, as everywhere else. */
    preferredAnchor?: AnchorChainId;
}): WithdrawDestination {
    return input.directAvailable && input.hypeGasOk === true
        ? 'hyperEvm'
        : (input.preferredAnchor ?? DEFAULT_ANCHOR_CHAIN_ID);
}

// --- live reads the executor polls between steps -------------------------------------------

/** The adapter's Core SPOT USDC in core 8dp (0 when the account has none). */
export async function readCoreSpotUsdc8(adapter: string): Promise<bigint> {
    const state = await hlInfo<{
        balances?: { coin?: string; total?: string }[];
    }>({ type: 'spotClearinghouseState', user: adapter }, { timeoutMs: 5_000 });
    const row = (state.balances ?? []).find(
        (balance) => balance?.coin?.toUpperCase() === 'USDC',
    );
    const text = String(row?.total ?? '0').trim();
    if (!/^\d+(\.\d+)?$/.test(text)) {
        return 0n;
    }
    const [whole, fraction = ''] = text.split('.');
    return BigInt(whole) * 100_000_000n + BigInt((fraction + '00000000').slice(0, 8));
}

/** ERC20 USDC balance of an address on HyperEVM (6dp). */
export async function readEvmUsdc6(
    usdc: string,
    holder: string,
): Promise<bigint> {
    return hyperEvmPublicClient.readContract({
        address: getAddress(usdc),
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [getAddress(holder)],
    });
}

/** The EOA's native HYPE balance, for the gas gate. */
export async function readHypeBalanceWei(eoa: string): Promise<bigint> {
    return hyperEvmPublicClient.getBalance({ address: getAddress(eoa) });
}

export function hasUsableAdapter(
    light: Pick<BalanceLightResponse, 'adapter'>,
): boolean {
    return !!light.adapter && getAddress(light.adapter) !== zeroAddress;
}
